import { useTheme } from '@emotion/react';
import { Collapse, useMediaQuery } from '@mui/material';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import * as reactIntl from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { userInstrumentsSlice, userSecuritiesAccountsSlice } from '!core/redux';
import { securityAccountSelectedSelector } from '!core/redux/userSecuritiesAccounts/userSecuritiesAccounts.selectors';
import { uuidSelector } from '»components/index';
import { TrlCardsList, TrlGrid, TrlTabContainer } from '»components/molecules';
import { TrlCardStockExchangeDet } from '»components/molecules/TrlCard';
import { TrlChart } from '»components/molecules/TrlChart';
import {
  Details,
  DocumentsList,
  LastTrades,
  MarketDepht,
} from '»components/molecules/TrlDetailsIntrument';
import { TabPanel } from '»components/molecules/TrlTabContainer';
import { PrivateView } from '»components/templates';
import { TrlInsertOrderModal } from '»components/templates/TrlInsertOrder';
import {
  useFooterSectionHeight,
  useGridRemainingHeight,
  useTitlesViewContext,
} from '»utils/customHooks';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { marketStockExchangeSlice } from './_marketStockExchange.slice';
import { columns } from './marketStockExchange.schema';
import {
  getCurrentStockExchangeTab,
  getIsLoading,
  getmarketStockExchange,
  getSelectedTitle,
  getWebAreas,
} from './marketStockExchange.seletors';
import {
  DesktopContentContainer,
  DetailLeft,
  DetailRight,
  DetailsContainer,
  MarketDetail,
  MarketStockExchangeContainer,
  WrapperCards,
} from './MarketStockExchange.styles';
import { TrlStockExchangeMarketTabs } from './TrlStockExchangeMarketTabs';

export const MarketStockExchange = () => {
  const dispatch = useDispatch();
  const intl = reactIntl.useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [valueDetail, setValueDetail] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [mainContentOffsetTop, setMainContentOffsetTop] = useState(false);

  const exportGridRef = useRef(null);
  const mainContentContainerRef = useRef(null);

  const selectedTitle = useSelector(getSelectedTitle);
  const currentTab = useSelector(getCurrentStockExchangeTab);

  const { titlesView } = useTitlesViewContext();
  const { gridMaxHeight, updateGridMaxHeight, detailsRef } =
    useFooterSectionHeight();

  const setFavStockExchangeMarket = ({ dataItem }) => {
    const isFavourite = dataItem.isFavourite === 'Y';

    dispatch(
      userInstrumentsSlice.actions.setFavInstrumentRequest({
        option: isFavourite ? 'D' : 'I',
        instrument: dataItem.instrument,
        symbol: dataItem.symbol,
        exchange: dataItem.exchange,
        securityType: dataItem.securityType,
      }),
    );
  };

  const stockExchangeColumns = columns({
    setFavourite: setFavStockExchangeMarket,
  });

  const handleChangeCurrentTab = (event, newValue) => {
    dispatch(marketStockExchangeSlice.actions.setCurrentTab(newValue));
  };

  const handleChangeDetail = (_, newValue) => {
    setValueDetail(newValue);
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const securityAccount = useSelector(securityAccountSelectedSelector);
  const uuid = useSelector(uuidSelector);
  const webAreas = useSelector(getWebAreas);
  const isPublicOfferDataLoading = useSelector(getIsLoading);
  const publicOfferData = useSelector(getmarketStockExchange).map(
    (dataItem) => ({
      selected: false,
      ...dataItem,
    }),
  );

  const getRemainingHeight = useCallback(
    ({ gridContent, rowsHeight }) => {
      const footerTop = detailsRef.current?.getBoundingClientRect().top;
      const gridContentTop = gridContent?.getBoundingClientRect().top;

      const availableHeight =
        typeof footerTop === 'number' && typeof gridContentTop === 'number'
          ? footerTop - gridContentTop
          : gridContent?.clientHeight || 0;

      return availableHeight - rowsHeight - 12 - 4; // 12 is space between grid bottom and footer top
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detailsRef, gridMaxHeight, expanded, currentTab],
  );

  const { remainingGridHeight, hasHorizontalScroll, hasVerticalScroll } =
    useGridRemainingHeight({
      containerRef: mainContentContainerRef,
      gridDataLength: publicOfferData.length,
      getRemainingHeight,
    });

  const footerDetailsHeight = 210;
  const nominalValueExcelColumnName = TrlFormattedMessagePlain({
    id: 'TRLT_UI-NOMINAL_VALUE',
  });
  const excelFileName =
    webAreas?.length > 0
      ? `${TrlFormattedMessagePlain({
          id: 'TRLT_UI-EXCHANGE',
        })}-${webAreas[currentTab].descr}`
      : '';

  useEffect(() => {
    if (webAreas !== undefined) {
      dispatch(marketStockExchangeSlice.actions.resetMarketStockExchange());
      const areaId = webAreas[currentTab].wearseq;
      dispatch(
        marketStockExchangeSlice.actions.marketStockExchangeRequest({
          webAreaId: areaId,
          getCharts: isMobile,
        }),
      );
    }
  }, [dispatch, securityAccount, currentTab, webAreas, uuid]);

  useEffect(() => {
    dispatch(
      marketStockExchangeSlice.actions.webAreasRequest({
        webPage: 'QUOTES_MARKET',
      }),
    );
    dispatch(
      userSecuritiesAccountsSlice.actions.userSecuritiesAccountsRequest(),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportExcel = useCallback(() => {
    const excelColumns = stockExchangeColumns.reduce((acc, column) => {
      if (
        column.field !== 'space' &&
        column.field !== 'dummy' &&
        column.field !== 'isFavourite' &&
        column.field !== 'minusCell' &&
        column.field !== 'plusCell'
      ) {
        // eslint-disable-next-line unused-imports/no-unused-vars
        let { children, headerCellOptions, setFavourite, ...newColumn } =
          column;

        if (headerCellOptions)
          newColumn.headerCellOptions = { ...headerCellOptions };

        if (newColumn.field === 'country') newColumn.field = newColumn.exchange;

        if (children) {
          const { field } = children[0];

          if (field === 'nominalValue' || field === 'lastDateTime') {
            const child = children[0];
            newColumn = {
              ...newColumn,
              ...child,
              title:
                field === 'nominalValue'
                  ? nominalValueExcelColumnName
                  : child.title,
            };
          }
        }

        acc.push(newColumn);
      }

      return acc;
    }, []);

    exportGridRef.current?.save(publicOfferData, excelColumns);
  }, [stockExchangeColumns, publicOfferData, nominalValueExcelColumnName]);

  useLayoutEffect(() => {
    if (mainContentContainerRef.current)
      setMainContentOffsetTop(mainContentContainerRef.current.offsetTop);
  }, [titlesView]);

  return (
    <PrivateView>
      <MarketStockExchangeContainer>
        <TrlInsertOrderModal />
        {webAreas?.length > 0 && (
          <TrlStockExchangeMarketTabs
            currentTab={currentTab}
            setCurrentTab={handleChangeCurrentTab}
            tabButtons={['viewSelector', 'downloadExcel']}
            downloadExcelButtonClick={handleExportExcel}
            isDownloadExcelButtonDisabled={
              publicOfferData.length === 0 || isPublicOfferDataLoading
            }
          >
            {isMobile ? (
              <TrlCardsList
                titles={publicOfferData}
                isLoadingTitles={isPublicOfferDataLoading}
              />
            ) : (
              <DesktopContentContainer
                ref={mainContentContainerRef}
                mainContentOffsetTop={mainContentOffsetTop}
              >
                <ExcelExport ref={exportGridRef} fileName={excelFileName} />
                {titlesView === 'table' ? (
                  <TrlGrid
                    columns={stockExchangeColumns}
                    data={publicOfferData}
                    selectedItem={selectedTitle}
                    selectedItemChange={(title) =>
                      dispatch(
                        marketStockExchangeSlice.actions.setSelectedTitle(
                          title,
                        ),
                      )
                    }
                    dataItemKey="symbol"
                    extraHeight={footerDetailsHeight}
                    maxHeight={`calc(100% - ${gridMaxHeight}px)`}
                    remainingGridHeight={remainingGridHeight}
                    hasHorizontalScroll={hasHorizontalScroll}
                    hasVerticalScroll={hasVerticalScroll}
                  />
                ) : (
                  <TrlCardsList
                    titles={publicOfferData}
                    isLoadingTitles={isPublicOfferDataLoading}
                    footerDetailsHeight={footerDetailsHeight}
                    maxHeight={`calc(100% - ${gridMaxHeight}px)`}
                  />
                )}
                <MarketDetail ref={detailsRef}>
                  <WrapperCards id="cardStockExchangeDet">
                    <TrlCardStockExchangeDet
                      onClick={handleExpandClick}
                      expand={expanded}
                      selectedItem={selectedTitle}
                    />
                  </WrapperCards>

                  <Collapse
                    in={expanded}
                    timeout="auto"
                    unmountOnExit
                    onEntered={updateGridMaxHeight}
                    onExited={updateGridMaxHeight}
                  >
                    <DetailsContainer>
                      <DetailLeft
                        id="collapseDetailLeft"
                        currentTab={valueDetail}
                      >
                        <TrlTabContainer
                          value={valueDetail}
                          variant="innerTabs"
                          handleChange={handleChangeDetail}
                          tabs={[
                            {
                              label: intl.formatMessage({
                                id: 'TRLT_UI-DEPTH',
                              }),
                            },
                            {
                              label: intl.formatMessage({
                                id: 'TRLT_UI-EVENT_TYPE',
                              }),
                            },
                            {
                              label: intl.formatMessage({
                                id: 'TRLT_UI-LAST_DETALS',
                              }),
                            },
                            {
                              label: intl.formatMessage({
                                id: 'TRLT_UI-DOCUMENTS',
                              }),
                            },
                          ]}
                        />
                        <TabPanel
                          className="stockExchangeDetailsTab"
                          value={valueDetail}
                          index={0}
                        >
                          <MarketDepht />
                        </TabPanel>
                        <TabPanel
                          className="stockExchangeDetailsTab"
                          value={valueDetail}
                          index={1}
                        >
                          <Details />
                        </TabPanel>
                        <TabPanel
                          className="stockExchangeDetailsTab"
                          value={valueDetail}
                          index={2}
                        >
                          <LastTrades />
                        </TabPanel>
                        <TabPanel
                          className="stockExchangeDetailsTab"
                          value={valueDetail}
                          index={3}
                        >
                          <DocumentsList />
                        </TabPanel>
                      </DetailLeft>
                      <DetailRight id="collapseDetailRight">
                        <TrlChart />
                      </DetailRight>
                    </DetailsContainer>
                  </Collapse>
                </MarketDetail>
              </DesktopContentContainer>
            )}
          </TrlStockExchangeMarketTabs>
        )}
      </MarketStockExchangeContainer>
    </PrivateView>
  );
};
